import React, { useState } from 'react';
import { XMarkIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { createTaskTemplateAdmin } from './templateManagementService';

interface CreateJurisdictionBaseModalProps {
  onClose: () => void;
  onCreated: (templateId: string) => void;
}

const US_STATES: Array<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

const PRACTICE_AREAS = [
  'family_law', 'civil_litigation', 'criminal_defense', 'estate_planning',
  'immigration', 'real_estate', 'employment_law', 'business_law',
  'personal_injury', 'bankruptcy', 'intellectual_property', 'tax_law',
];

const CreateJurisdictionBaseModal: React.FC<CreateJurisdictionBaseModalProps> = ({ onClose, onCreated }) => {
  const [stateCode, setStateCode] = useState('');
  const [courtName, setCourtName] = useState('');
  const [practiceArea, setPracticeArea] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const autoName = stateCode && practiceArea
    ? `${practiceArea.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} - ${US_STATES.find(s => s.code === stateCode)?.name || stateCode}${courtName ? ` (${courtName})` : ''}`
    : '';

  const displayName = name || autoName;
  const canSubmit = stateCode && practiceArea && displayName && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setError(null);

      const template = await createTaskTemplateAdmin({
        name: displayName,
        description: description || undefined,
        practice_area: practiceArea,
        jurisdiction: {
          level: 'state',
          state_code: stateCode,
          court_name: courtName || undefined,
        },
        jurisdiction_code: `${stateCode}`,
        template_kind: 'jurisdiction_base',
        is_system_template: true,
        is_active: true,
        lifecycle_status: 'draft',
        inherits_updates: true,
        tasks: [],
        phases: [{ name: 'Initial Filing', description: 'Filing and service phase', order: 1, tasks: [] }],
      });

      onCreated(template.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create template');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={onClose} style={{ animation: 'lex-overlay-in 150ms ease-out forwards' }} />

      <div className="relative z-10 w-full max-w-lg mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden" style={{ animation: 'lex-modal-in 220ms ease-out forwards' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-lex-accent-soft dark:bg-lex-accent-soft rounded-lg">
              <PlusCircleIcon className="h-5 w-5 text-lex-accent dark:text-lex-accent" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create Jurisdiction Base Template</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {/* State */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              State <span className="text-red-500">*</span>
            </label>
            <select
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              className="w-full outline-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-lex-accent text-sm"
            >
              <option value="">Select state...</option>
              {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </div>

          {/* Court Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Court Name <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={courtName}
              onChange={(e) => setCourtName(e.target.value)}
              placeholder="e.g., San Diego Superior Court"
              className="w-full outline-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-lex-accent text-sm"
            />
          </div>

          {/* Practice Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Practice Area <span className="text-red-500">*</span>
            </label>
            <select
              value={practiceArea}
              onChange={(e) => setPracticeArea(e.target.value)}
              className="w-full outline-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-lex-accent text-sm"
            >
              <option value="">Select practice area...</option>
              {PRACTICE_AREAS.map(pa => (
                <option key={pa} value={pa}>{pa.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Template Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={autoName || 'Auto-generated from selections'}
              className="w-full outline-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-lex-accent text-sm"
            />
            {!name && autoName && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Will use: {autoName}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this template..."
              rows={2}
              className="w-full outline-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-lex-accent text-sm resize-none"
            />
          </div>

          {/* Info */}
          <div className="p-3 rounded-lg bg-lex-accent-soft dark:bg-lex-accent-soft border border-lex-accent dark:border-lex-accent text-xs text-lex-accent dark:text-lex-accent">
            This creates a jurisdiction base template with a single default phase. You can add tasks, phases, and forms by editing the template after creation.
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-lex-accent-contrast bg-lex-accent hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Creating...
              </>
            ) : (
              <>
                <PlusCircleIcon className="h-4 w-4" />
                Create Template
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateJurisdictionBaseModal;
